// Item tooltip text: the lines under an item's name, with the UI's colours.
import { Binding, Coin, ContainerId, GameConstants, ItemQuality, JewelryKind, OffhandKind, ToolKind, WeaponClass } from '../../../../shared/src/constants';
import { content, type ItemDefinition } from '../content';
import { Money } from './economy';
import { Defense, Equipment, Inventory, ToolBelt, consumableCooldownSeconds, isArmor, isConsumable, isSellable, type ItemStack } from './items';
import { f32, oneDecimal } from '../../../../shared/src/rules/numbers';
import { Motor } from '../../../../shared/src/motor';

export const UiColors = {
  text: '#ece2d6', dim: '#a0928c', accent: '#f9923c', bonus: '#96dc5a', worse: '#eb5f55', soulbound: '#a0b9ff',
  description: '#8e8682', hint: '#d6c496', muted: '#968c88', ready: '#96dc5a', lost: '#f09646',
  coin(coin: Coin): string {
    return coin === Coin.Silver ? '#cdd4de' : coin === Coin.Gold ? '#f5cd50' : coin === Coin.Platinum ? '#b9ebf5' : '#dc9664';
  },
  quality(q: ItemQuality): string {
    switch (q) {
      case ItemQuality.Junk: return '#9d9d9d';
      case ItemQuality.Uncommon: return '#1edc3c';
      case ItemQuality.Rare: return '#2882ff';
      case ItemQuality.Epic: return '#be50ff';
      case ItemQuality.Legendary: return '#ff8c14';
      default: return '#ffffff';
    }
  },
};

/** One line: t = text, c = its colour, v = a value on the right (vc its colour), ch = a change when comparing. Or a gap. */
export interface TipLine { t?: string; c?: string; v?: string; vc?: string; ch?: string; chc?: string; gap?: true }

export class Tip {
  readonly lines: TipLine[] = [];

  line(text: string, color: string, value?: string, valueColor?: string, change?: string, changeColor?: string): this {
    const l: TipLine = { t: text, c: color };
    if (value !== undefined) l.v = value;
    l.vc = valueColor ?? color;
    if (change !== undefined) l.ch = change;
    if (changeColor !== undefined) l.chc = changeColor;
    this.lines.push(l);
    return this;
  }

  gap(): this { this.lines.push({ gap: true }); return this; }

  plus(label: string, amount: number, color: string): this {
    return this.line(label, color, `+${amount}`, amount > 0 ? UiColors.bonus : UiColors.description);
  }

  price(label: string, copper: number): this {
    return this.line(label, UiColors.dim, Money.format(copper), UiColors.coin(Money.coins(copper)[0].coin));
  }
}

/** A stat shown (and compared) on a tooltip. lowerIsBetter: a cost, so going up compares red. */
export interface Stat { name: string; value: number; text: string; highlight: boolean; lowerIsBetter?: boolean }

/** The stats shown (and compared) for an item. */
export function statsOf(def: ItemDefinition): Stat[] {
  const stats: Stat[] = [];
  if (isArmor(def) || def.armorValue > 0) stats.push({ name: 'Armor', value: def.armorValue, text: `+${def.armorValue}`, highlight: true });
  if (def.blockArmor > 0) stats.push({ name: 'Armor while blocking', value: def.blockArmor, text: `+${def.blockArmor}`, highlight: true });
  // A staff's own stats are unused: the spell scroll beside it says what a cast does.
  const attack = def.spell ?? (def.tool === ToolKind.Sword && def.weapon?.weaponClass !== WeaponClass.Staff ? def.weapon : null);
  const zone = def.spell?.zone;
  if (zone) {
    const tiles = (px: number) => f32(px / GameConstants.tileSize);
    stats.push({ name: 'Healing per second', value: zone.healPerSecond, text: `${zone.healPerSecond}`, highlight: true });
    stats.push({ name: 'Lasts', value: zone.durationSeconds, text: `${oneDecimal(zone.durationSeconds)}s`, highlight: false });
    stats.push({ name: 'Radius', value: tiles(zone.radius), text: `${oneDecimal(tiles(zone.radius))} tiles`, highlight: false });
  }
  if (attack) {
    const missiles = def.spell?.missiles ?? 1;
    if (!zone) stats.push({ name: 'Damage', value: attack.damage, text: missiles > 1 ? `${attack.damage} x${missiles}` : `${attack.damage}`, highlight: true });
    const rate = f32(1 / f32(attack.cooldownSeconds));
    if (zone) stats.push({ name: 'Cooldown', value: attack.cooldownSeconds, text: `${oneDecimal(attack.cooldownSeconds)}s`, highlight: false, lowerIsBetter: true });
    else stats.push({ name: def.spell ? 'Casts per second' : 'Attacks per second', value: rate, text: oneDecimal(rate), highlight: false });
    if (attack.projectile || zone) {
      const tiles = f32(attack.reach / GameConstants.tileSize);
      stats.push({ name: 'Range', value: tiles, text: `${oneDecimal(tiles)} tiles`, highlight: false });
    }
    if (attack.manaCost > 0)
      stats.push({ name: def.spell ? 'Mana per cast' : 'Mana per shot', value: attack.manaCost, text: `${attack.manaCost}`, highlight: false, lowerIsBetter: true });
  }
  if (isConsumable(def)) {
    stats.push({ name: 'Restores health', value: def.heals, text: `+${def.heals}`, highlight: true });
    stats.push({ name: 'Cooldown', value: consumableCooldownSeconds, text: `${oneDecimal(consumableCooldownSeconds)}s`, highlight: false });
  }
  return stats;
}

/** What an empty equipment slot compares as. */
export const nothingWorn: ItemDefinition = {
  id: 0, key: '', name: '', description: '', maxStack: 0, quality: ItemQuality.Common, binding: Binding.None, icon: '',
  tool: ToolKind.None, armor: 0, armorValue: 0, blockArmor: 0, look: '', sellPrice: 0, heals: 0, weapon: null, offhand: OffhandKind.None, spell: null,
  jewelry: JewelryKind.None, unique: false,
};

/** What's worn in the slot an item would go in, and the slot's name; null for items that aren't worn. */
export function equippedFor(inv: Inventory, def: ItemDefinition): { worn: ItemStack; slot: string } | null {
  const target = inv.equipSlot(def);
  if (!target) return null;
  const [c, i] = target;
  return { worn: inv.get(c, i), slot: c === ContainerId.Armor ? Equipment.slotName(i) : ToolBelt.slotName(i) };
}

/** The rarity a tooltip shows beside an item's name. */
export const qualityName = (q: ItemQuality) => ItemQuality[q] ?? String(q);

/** An item's tooltip lines (under its name and rarity). compareTo: the worn item to show changes against; offerCompare: say Shift compares. */
export function itemLines(def: ItemDefinition, hint: string, bound: boolean, compareTo: ItemDefinition | null = null,
  offerCompare = false, count = 1): Tip {
  const tip = new Tip();
  // The binding, with Unique on the right.
  const binding = bound ? 'Soulbound' : def.binding === Binding.OnEquip ? 'Binds when equipped' : def.binding === Binding.OnPickup ? 'Binds when picked up' : '';
  const unique = def.unique ? 'Unique' : undefined;
  if (binding || unique) tip.line(binding, bound ? UiColors.soulbound : UiColors.dim, unique, unique && UiColors.text);

  const theirs = compareTo ? statsOf(compareTo) : null;
  for (const stat of statsOf(def)) {
    let change: string | undefined, changeColor: string | undefined;
    if (theirs) {
      const diff = f32(stat.value - (theirs.find(t => t.name === stat.name)?.value ?? 0));
      const same = Math.abs(diff) < f32(0.05);
      change = same ? '(=)' : `(${diff > 0 ? '+' : '-'}${oneDecimal(Math.abs(diff))})`;
      changeColor = same ? UiColors.description : (diff > 0) !== !!stat.lowerIsBetter ? UiColors.bonus : UiColors.worse;
    }
    tip.line(stat.name, UiColors.text, stat.text, stat.highlight ? UiColors.bonus : UiColors.text, change, changeColor);
  }
  if (def.weapon && def.weapon.ammo) tip.line(`Each shot uses 1 ${content().item(def.weapon.ammo)?.name ?? 'ammunition'} from your offhand slot`, UiColors.dim);
  if (def.weapon?.weaponClass === WeaponClass.Staff) tip.line('Casts the spell scroll in your offhand slot', UiColors.dim);
  if (def.weapon?.weaponClass === WeaponClass.TwoHanded) tip.line('Leaves no hand free for the offhand slot', UiColors.dim);
  if (def.offhand === OffhandKind.Shield)
    tip.line(`Hold right-click to block: each hit blocked costs ${Defense.blockStaminaUnits / Motor.staminaUnitsPerPoint} stamina`, UiColors.dim);
  if (compareTo) tip.line(compareTo === nothingWorn ? 'Compared with nothing worn' : `Compared with ${compareTo.name}`, UiColors.description);
  if (def.description.length > 0) tip.gap().line(def.description, UiColors.description);
  if (isSellable(def)) {
    tip.gap().price(count > 1 ? 'Sell price (each)' : 'Sell price', def.sellPrice);
    if (count > 1) tip.price(`Stack of ${count}`, def.sellPrice * count);
  }
  if (hint.length > 0 || offerCompare) tip.gap();
  if (hint.length > 0) tip.line(hint, UiColors.hint);
  if (offerCompare) tip.line('Hold Shift to compare', UiColors.dim);
  return tip;
}
